import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../../seo/Seo';
import { softwareSchema } from '../../seo/schema';
import Icon from '../home/Icon';
import './ProductPage.css';

// A product landing page assembled from a plain data object (see
// online-store/onlineStoreData.js for the shape). Each page supplies its own
// hero and "books" illustrations; everything else is shared so the pages read
// as one family with the Point of Sale page.

// ['Plain words', 'italic accent.'] → Plain words <em>italic accent.</em>
const Title = ({ parts }) => {
  if (!Array.isArray(parts)) return parts;
  const [plain, accent] = parts;
  return <>{plain}{accent ? <> <em>{accent}</em></> : null}</>;
};

const useReveal = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.pp-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    const failsafe = setTimeout(() => els.forEach((el) => el.classList.add('in')), 1200);
    return () => { io.disconnect(); clearTimeout(failsafe); };
  }, []);
};

const Head = ({ eyebrow, title, lead, center, light }) => (
  <div className={`pp-head ${center ? 'pp-head--center' : ''} pp-reveal`}>
    {eyebrow && <span className={`pp-eyebrow ${light ? 'pp-eyebrow--light' : ''}`}>{eyebrow}</span>}
    <h2 className={`pp-h2 ${light ? 'pp-h2--light' : ''}`}><Title parts={title} /></h2>
    {lead && <p className={`pp-lead ${center ? 'pp-lead--center' : ''} ${light ? 'pp-lead--light' : ''}`}>{lead}</p>}
  </div>
);

const DEFAULT_SECONDARY = { label: 'Book a demo', href: '/contact-us' };

const ProductPage = ({ page, heroVisual, booksVisual }) => {
  const navigate = useNavigate();
  const secondary = page.hero.secondary || DEFAULT_SECONDARY;
  useReveal();
  const go = (href) => (e) => { e.preventDefault(); navigate(href); };

  const accentStyle = {
    '--pp-accent': page.accent.color,
    '--pp-accent-soft': page.accent.soft,
    '--pp-accent-border': page.accent.border,
    '--pp-accent-glow': page.accent.glow,
  };

  return (
    <div className="pp-page" style={accentStyle} data-page={page.key}>
      <Seo
        path={page.path}
        faqs={page.faqs}
        schema={[softwareSchema({ name: page.schema.name, subCategory: page.schema.subCategory, description: page.schema.description, path: page.path })]}
      />

      {/* ── Hero ─────────────────────────────── */}
      <section className="pp-hero">
        <div className="pp-hero-grid" aria-hidden="true" />
        <div className="pp-hero-blob pp-hero-blob--blue" aria-hidden="true" />
        <div className="pp-hero-blob pp-hero-blob--accent" aria-hidden="true" />
        <div className="pp-container pp-hero-inner">
          <div className="pp-hero-text">
            <span className="pp-hero-badge"><Icon name={page.hero.badge.icon} size={14} />{page.hero.badge.label}</span>
            <h1 className="pp-hero-title"><Title parts={page.hero.title} /></h1>
            <p className="pp-hero-sub">{page.hero.sub}</p>
            <div className="pp-hero-actions">
              <a href={page.hero.primary.href} className="pp-btn pp-btn--primary pp-btn--lg" onClick={go(page.hero.primary.href)}>{page.hero.primary.label}</a>
              <a href={secondary.href} className="pp-btn pp-btn--ghost pp-btn--lg" onClick={go(secondary.href)}>{secondary.label}</a>
            </div>
            <p className="pp-hero-meta">{page.hero.meta}</p>
          </div>
          <div className="pp-hero-visual">{heroVisual}</div>
        </div>
      </section>

      {/* ── Flow ─────────────────────────────── */}
      {page.flow && (
        <section className="pp-section pp-section--alt">
          <div className="pp-container">
            <Head eyebrow={page.flow.eyebrow} title={page.flow.title} lead={page.flow.lead} center />
            <ol className="pp-flow pp-reveal" style={{ '--pp-flow-cols': page.flow.steps.length }}>
              {page.flow.steps.map((s, i) => (
                <li className="pp-flow-step" key={s.title}>
                  <div className="pp-flow-top">
                    <span className="pp-flow-icon"><Icon name={s.icon} size={18} /></span>
                    <span className="pp-flow-num">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="pp-flow-title">{s.title}</h3>
                  <p className="pp-flow-desc">{s.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      {/* ── Features ─────────────────────────────── */}
      <section className="pp-section">
        <div className="pp-container">
          <Head eyebrow={page.features.eyebrow} title={page.features.title} lead={page.features.lead} />
          <div className="pp-features pp-reveal">
            {page.features.items.map((f) => (
              <article className="pp-feature" key={f.title}>
                <span className="pp-feature-icon"><Icon name={f.icon} size={19} /></span>
                <h3 className="pp-feature-title">{f.title}</h3>
                <p className="pp-feature-desc">{f.desc}</p>
              </article>
            ))}
          </div>
          {page.features.note && (
            <p className="pp-note pp-reveal">
              <span className="pp-note-tag">{page.features.note.tag}</span>{page.features.note.text}
            </p>
          )}
        </div>
      </section>

      {/* ── Connected to your books ─────────────────────────────── */}
      <section className="pp-section pp-section--dark">
        <div className="pp-container">
          <div className="pp-books pp-reveal">
            <div>
              <span className="pp-eyebrow pp-eyebrow--light">{page.books.eyebrow}</span>
              <h2 className="pp-h2 pp-h2--light"><Title parts={page.books.title} /></h2>
              <p className="pp-lead pp-lead--light">{page.books.lead}</p>
              <div className="pp-books-points">
                {page.books.points.map((b) => (
                  <div className="pp-books-point" key={b.title}>
                    <span className="pp-books-icon"><Icon name={b.icon} size={16} /></span>
                    <div>
                      <h3 className="pp-books-title">{b.title}</h3>
                      <p className="pp-books-desc">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>{booksVisual}</div>
          </div>
          {page.books.band && (
            <div className="pp-band pp-reveal">
              <div className="pp-band-text">
                <span className="pp-band-icon"><Icon name={page.books.band.icon} size={18} /></span>
                <div>
                  <h3 className="pp-band-title">{page.books.band.title}</h3>
                  <p className="pp-band-desc">{page.books.band.desc}</p>
                </div>
              </div>
              <ul className="pp-band-list">
                {page.books.band.points.map((p) => (
                  <li key={p}><span className="pp-tick" aria-hidden="true">✓</span>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* ── Set up ─────────────────────────────── */}
      <section className="pp-section">
        <div className="pp-container">
          <Head eyebrow={page.setup.eyebrow} title={page.setup.title} lead={page.setup.lead} center />
          <ol className="pp-steps pp-reveal">
            {page.setup.steps.map((s, i) => (
              <li className="pp-step" key={s.title}>
                <div className="pp-step-top">
                  <span className="pp-step-num">{i + 1}</span>
                  <span className="pp-step-icon"><Icon name={s.icon} size={18} /></span>
                </div>
                <h3 className="pp-step-title">{s.title}</h3>
                <p className="pp-step-desc">{s.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Use cases ─────────────────────────────── */}
      {page.cases && (
        <section className="pp-section pp-section--alt">
          <div className="pp-container">
            <Head eyebrow={page.cases.eyebrow} title={page.cases.title} />
            <div className="pp-cases pp-reveal">
              {page.cases.items.map((c) => (
                <div className="pp-case" key={c.name}>
                  <span className="pp-case-icon"><Icon name={c.icon} size={20} /></span>
                  <div>
                    <h3 className="pp-case-name">{c.name}</h3>
                    <p className="pp-case-desc">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ─────────────────────────────── */}
      <section className="pp-section">
        <div className="pp-container pp-container--narrow">
          <Head eyebrow="Questions" title={page.faqTitle} center />
          <div className="pp-faq pp-reveal">
            {page.faqs.map((f) => (
              <details className="pp-faq-item" key={f.q}>
                <summary className="pp-faq-q">{f.q}<span className="pp-faq-plus" aria-hidden="true">+</span></summary>
                <p className="pp-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
          {page.related && (
            <nav className="pp-related pp-reveal" aria-label="Related products">
              {page.related.map((r) => (
                <a key={r.href} href={r.href} onClick={go(r.href)}>{r.label}<Icon name="arrow-right" size={14} /></a>
              ))}
            </nav>
          )}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────── */}
      <section className="pp-cta">
        <div className="pp-cta-blob" aria-hidden="true" />
        <div className="pp-container pp-cta-inner">
          <h2 className="pp-cta-title">{page.cta.title}</h2>
          <p className="pp-cta-sub">{page.cta.sub}</p>
          <div className="pp-cta-actions">
            <a href={page.hero.primary.href} className="pp-btn pp-btn--primary pp-btn--lg" onClick={go(page.hero.primary.href)}>{page.hero.primary.label}</a>
            <a href={secondary.href} className="pp-btn pp-btn--outline pp-btn--lg" onClick={go(secondary.href)}>{secondary.label}</a>
          </div>
          <p className="pp-cta-meta">Raya Software Solutions · Built for Indian businesses</p>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
